import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Mountain, Mail, Lock, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_comunelaval-halls/artifacts/khvga6ci_Cuns%C3%ABi%20de%20Comun%2020251017_124442.jpg";

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [halls, setHalls] = useState([]);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_URL}/halls`).then(res => setHalls(res.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Accesso effettuato!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'accesso');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Form */}
      <div className="flex items-center justify-center p-8 lg:p-12">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center space-y-2">
            <div className="flex items-center justify-center gap-2 mb-6">
              <img src={LOGO_URL} alt="Comune di La Val" className="w-12 h-12 rounded-full object-cover" />
              <h1 className="font-heading font-extrabold text-3xl tracking-tight">
                <span className="font-bold">Salf</span>
                <span className="font-light"> manifestaziuns</span>
              </h1>
            </div>
            <h2 className="font-heading font-semibold text-2xl">Benvenuto</h2>
            <p className="text-muted-foreground">Accedi al tuo account</p>
          </div>

          <Card className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" data-testid="email-label">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tuo@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" data-testid="password-label">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10"
                    data-testid="password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full btn-scale"
                disabled={loading}
                data-testid="login-button"
              >
                {loading ? 'Accesso...' : 'Accedi'}
              </Button>
            </form>
          </Card>

          <p className="text-center text-sm text-muted-foreground">
            Non hai un account?{' '}
            <Link to="/register" className="text-primary hover:underline font-medium" data-testid="register-link">
              Registrati qui
            </Link>
          </p>

          {/* Public calendar links */}
          {halls.length > 0 && (
            <div className="space-y-3 p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border-2 border-green-500">
              <p className="text-center text-sm sm:text-base font-bold text-green-800 dark:text-green-200">
                Consulta i calendari senza registrarti
              </p>
              <div className="flex flex-col gap-2">
                {halls.map(hall => (
                  <Button
                    key={hall.id}
                    size="lg"
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold text-sm sm:text-base py-3"
                    onClick={() => navigate(`/public/calendar/${hall.id}`)}
                    data-testid={`public-calendar-${hall.id}`}
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    {hall.name}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right side - Hero Image */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1763985357104-af320fc8153a"
          alt="La Val Dolomites"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-gradient flex items-center justify-center p-12">
          <div className="text-center text-white space-y-4">
            <h2 className="font-heading font-bold text-4xl lg:text-5xl">
              Comune di La Val
            </h2>
            <p className="text-xl opacity-90">Sistema di prenotazione sale manifestazioni</p>
          </div>
        </div>
      </div>
    </div>
  );
}